import { html } from '@design.estate/dees-element';
import { DeesModal } from './dees-modal.js';
import { demoPage, demoSection, demoOutput, demoCode, demoStyles, showDemoResult } from '../../../demos/templates.js';
import { createWorkspace, updateWorkspace, workspaceGeneralFields, workspacePrivacyFields, workspaceForm, type IDemoWorkspace } from '../../../demos/workspace.js';
import type { DeesForm } from '../../00group-form/dees-form/dees-form.js';
import type { DeesInputText } from '../../00group-input/dees-input-text/dees-input-text.js';

/** The same draft survives navigation; only Save hands it back to the caller. */
export const openSettingsDemo = async (options: { initial?: IDemoWorkspace; onSave?: (data: IDemoWorkspace) => void } = {}) => {
  let draft = { ...(options.initial ?? createWorkspace()) };
  const sections = {
    general: { title: 'General', description: 'Make this workspace feel like yours.' },
    privacy: { title: 'Privacy', description: 'Decide what to share.' },
  };
  let active: keyof typeof sections = 'general';
  let modal: DeesModal;
  const capture = async () => {
    await modal.updateComplete;
    const form = modal.shadowRoot!.querySelector<DeesForm>('dees-form')!;
    draft = updateWorkspace(draft, await form.collectFormData());
  };
  // Distinct templates create a fresh form when its set of inputs changes.
  const content = () => active === 'general'
    ? html`<dees-form grouped>${workspaceGeneralFields(draft)}</dees-form>`
    : html`<dees-form grouped>${workspacePrivacyFields(draft)}</dees-form>`;
  const sidebar = () => html`
    <style>
      .settings-brand { padding: 0 12px 20px; font-size: 17px; font-weight: 600; letter-spacing: -.02em; }
      .settings-nav { display: grid; gap: 3px; }
      .settings-nav dees-button { display: block; --dees-color-button-secondary-bg: var(--dees-color-nav-selected); --dees-color-button-secondary-border: transparent; }
      .settings-nav dees-button::part(button) { justify-content: flex-start; height: 30px; padding: 0 12px; font-size: 13px; gap: 10px; border-radius: var(--dees-radius-lg); }
      @media(max-width:600px) { .settings-brand { display:none; } .settings-nav { grid-template-columns:1fr 1fr; padding:3px; border-radius:var(--dees-radius-lg); background:var(--dees-color-fill-tertiary); } }
    </style>
    <div class="settings-brand">Settings</div>
    <nav aria-label="Settings sections" class="settings-nav">
      ${(['general', 'privacy'] as const).map((key) => html`
        <dees-button full-width .type=${active === key ? 'secondary' : 'ghost'}
          aria-current=${active === key ? 'page' : 'false'} .text=${sections[key].title}
          .icon=${key === 'general' ? 'lucide:slidersHorizontal' : 'lucide:lockKeyhole'}
          @clicked=${async () => {
            if (active === key) return;
            await capture();
            active = key;
            modal.heading = sections[key].title;
            modal.subheading = sections[key].description;
            modal.content = content();
            modal.sidebar = sidebar();
          }}></dees-button>`)}
    </nav>`;
  modal = await DeesModal.createAndShow({
    heading: sections.general.title, subheading: sections.general.description,
    sidebar: sidebar(), width: 780, content: content(),
    menuOptions: [
      { name: 'Cancel', action: (instance) => instance!.destroy() },
      { name: 'Save changes', action: async () => {
        await capture();
        if (!draft.name.trim()) {
          active = 'general';
          modal.heading = sections.general.title;
          modal.subheading = sections.general.description;
          modal.content = content();
          modal.sidebar = sidebar();
          await modal.updateComplete;
          const name = modal.shadowRoot!.querySelector<DeesInputText>('[key="name"]')!;
          await name.updateComplete;
          name.validationState = 'invalid';
          name.validationText = 'Enter a workspace name.';
          name.focus();
          return;
        }
        options.onSave?.(draft);
        await modal.destroy();
      } },
    ],
  });
  return modal;
};

const openExample = (options: Partial<Parameters<typeof DeesModal.createAndShow>[0]>) => DeesModal.createAndShow({
  heading: 'Workspace details',
  content: html`<p>Review the details, then close this window to return to the catalog.</p>`,
  menuOptions: [{ name: 'Done', action: (modal) => modal!.destroy() }],
  ...options,
});

export const demoFunc = () => demoPage('Modal', 'Focused windows with native keyboard behavior, a clear action area, and room for settings that grow.', html`
  ${demoSection('Workspace settings', 'Switch between General and Privacy. A fixed height of 400px on desktop and 520px on narrow screens keeps the window steady.', html`
    <div class="dees-demo-row"><dees-button type="accent" text="Open settings" @clicked=${(event: Event) => {
      const trigger = event.currentTarget as Element;
      return openSettingsDemo({ onSave: (data) => showDemoResult(trigger, data) });
    }}></dees-button></div>${demoOutput('Save changes to inspect the workspace draft.')}
    ${demoCode("await DeesModal.createAndShow({\n  heading: 'General', sidebar, content, menuOptions,\n  width: 780, // sidebar height defaults to 400px, or 520px at <=600px\n});")}`)}
  ${demoSection('Window sizes', 'Choose a width for the task. Every window is clamped to the available viewport.', html`
    <div class="dees-demo-row">
      ${(['small', 'medium', 'large', 'fullscreen'] as const).map((width) => html`<dees-button .text=${width} @clicked=${() => openExample({ width, heading: `${width[0].toUpperCase()}${width.slice(1)} window`,
        content: width === 'medium' ? html`<style>${demoStyles}</style>${workspaceForm()}` : html`<p>This ${width} window keeps its heading and actions visible when the content scrolls.</p>`,
      })}></dees-button>`)}
      <dees-button text="Custom 700px" @clicked=${() => openExample({ width: 700 })}></dees-button>
      <dees-button text="Max 600px" @clicked=${() => openExample({ width: 'large', maxWidth: 600 })}></dees-button>
      <dees-button text="Min 400px" @clicked=${() => openExample({ width: 300, minWidth: 400 })}></dees-button>
      <dees-button text="Mobile fullscreen" @clicked=${() => openExample({ width: 'large', mobileFullscreen: true })}></dees-button>
    </div>`)}
  ${demoSection('Header & actions', 'Try a nested help window, alternate header controls, and wrapping footer actions.', html`
    <div class="dees-demo-row">
      <dees-button text="With help" @clicked=${() => openExample({ showHelpButton: true, onHelp: () => openExample({ heading: 'Workspace help', width: 'small', content: html`<p>Press Escape to return to the workspace window. Your keyboard focus returns with you.</p>` }).then(() => {}) })}></dees-button>
      <dees-button text="Help only" @clicked=${() => openExample({ showCloseButton: false, showHelpButton: true, onHelp: () => openExample({ heading: 'Help', width: 'small' }).then(() => {}) })}></dees-button>
      <dees-button text="Clean header" @clicked=${() => openExample({ showCloseButton: false })}></dees-button>
      <dees-button text="No footer" @clicked=${() => openExample({ menuOptions: [] })}></dees-button>
      <dees-button text="Three actions" @clicked=${() => openExample({ menuOptions: ['Cancel', 'Save a copy', 'Save changes'].map((name) => ({ name, action: (modal) => modal!.destroy() })) })}></dees-button>
      <dees-button text="Long labels" @clicked=${() => openExample({ menuOptions: ['Discard all changes', 'Save and continue editing'].map((name) => ({ name, action: (modal) => modal!.destroy() })) })}></dees-button>
    </div>`)}
  ${demoSection('Ask for one value', 'A prompt states what confirming does and keeps its own error on the field.', html`
    <div class="dees-demo-row">
      <dees-button text="Rename workspace" @clicked=${async (event: Event) => {
        const trigger = event.currentTarget as Element;
        const name = await DeesModal.prompt({
          heading: 'Rename workspace', label: 'Workspace name', value: 'Northstar',
          description: 'Everyone in this workspace sees the new name. Links and invitations keep working.',
          confirmLabel: 'Rename',
        });
        showDemoResult(trigger, name === null ? 'Dismissed' : `Renamed to ${name}`);
      }}></dees-button>
      <dees-button type="destructive" text="Close workspace" @clicked=${async (event: Event) => {
        const trigger = event.currentTarget as Element;
        const confirmation = await DeesModal.prompt({
          heading: 'Close Northstar?', label: 'Type northstar to confirm',
          description: html`Closing a workspace removes its services, files and invitations. This cannot be undone.
            Type <strong>northstar</strong> to confirm.`,
          emptyValueMessage: 'Type the workspace name to confirm.',
          confirmLabel: 'Close workspace',
          validate: (value) => value === 'northstar' ? undefined : 'That is not the workspace name.',
        });
        showDemoResult(trigger, confirmation === null ? 'Dismissed' : 'Workspace closed');
      }}></dees-button>
    </div>${demoOutput('Confirm or dismiss a prompt to see its result.')}
    ${demoCode("await DeesModal.prompt({\n  heading: 'Close Northstar?', label: 'Type northstar to confirm',\n  description: 'Closing a workspace removes its services, files and invitations.',\n  emptyValueMessage: 'Type the workspace name to confirm.',\n  validate: (value) => value === 'northstar' ? undefined : 'That is not the workspace name.',\n});")}`)}
  ${demoSection('Actions that take a moment', 'One action runs at a time: its button shows the busy face, the others wait, and a failure hands the dialog back.', html`
    <div class="dees-demo-row">
      <dees-button text="Slow save" @clicked=${(event: Event) => {
        const trigger = event.currentTarget as Element;
        let runs = 0;
        return openExample({
          heading: 'Issue invoice', content: html`<p>Issuing takes a moment. Press <strong>Issue</strong> twice — it still runs once.</p>`,
          menuOptions: [
            { name: 'Cancel', action: (modal) => modal!.destroy() },
            { name: 'Issue', action: async (modal) => {
              runs++;
              await new Promise((resolve) => setTimeout(resolve, 1500));
              showDemoResult(trigger, `Issued ${runs} time${runs === 1 ? '' : 's'}`);
              await modal!.destroy();
            } },
          ],
        });
      }}></dees-button>
      <dees-button text="Failing action" @clicked=${() => openExample({
        heading: 'Publish workspace', content: html`<p>This action rejects. The dialog stays open and the button becomes usable again.</p>`,
        menuOptions: [
          { name: 'Cancel', action: (modal) => modal!.destroy() },
          { name: 'Publish', action: async () => { throw new Error('The publishing service is unavailable.'); } },
        ],
      })}></dees-button>
    </div>${demoOutput('Press Issue twice in a row to see it run once.')}`)}
  ${demoSection('Long content', 'A predetermined height keeps the frame still. The content scrolls; the heading and actions stay in place.', html`
    <div class="dees-demo-row">
      <dees-button text="Long article" @clicked=${() => openExample({ heading: 'Workspace guide', height: 480, content: html`${Array.from({ length: 20 }, (_, index) => html`<p><strong>${index + 1}. Keep work organized.</strong> Give your workspace a recognizable name, choose who can see it, and review notification preferences with your team.</p>`)}` })}></dees-button>
      <dees-button text="Long list" @clicked=${() => openExample({ heading: 'Review queue', width: 'small', content: html`<ol>${Array.from({ length: 80 }, (_, index) => html`<li>Review item ${index + 1}</li>`)}</ol>` })}></dees-button>
      <dees-button text="Tall form" @clicked=${() => openExample({ heading: 'Team directory', height: 480, content: html`<dees-form>${Array.from({ length: 25 }, (_, index) => html`<dees-input-text .key=${`member${index}`} .label=${`Team member ${index + 1}`}></dees-input-text>`)}</dees-form>` })}></dees-button>
    </div>`)}
`);
